// Users module for Docospot
// Handles user management with infinite scroll, filtering, and sorting

import { t } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { apiCall } from '../core/api.js';
import { showError } from '../core/ui.js';
import { updatePageTitle, updateActiveNavigation } from './navigation.js';
import { getEffectiveAccountId, getCurrentUser } from '../utils/helpers.js';

// Infinite scroll state
export const usersInfiniteScroll = {
    currentPage: 1,
    pageSize: 50,
    allUsers: [],
    totalUsers: 0,
    hasMore: true,
    isLoading: false
};

// Table sort state
export const tableSortState = {
    users: {
        column: null,
        direction: 'asc'
    }
};

/**
 * Display the users page with filters and table
 */
export async function showUsers() {
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    if (!auth.isAuthenticated()) {
        showError('Please login to view users');
        return;
    }

    mainContent.innerHTML = `
        <!-- Enhanced Filter Row - Sticky -->
        <div class="sticky top-16 z-30 mb-0 bg-gray-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3">
            <div class="bg-gray-200 shadow rounded-lg p-4">
                <!-- Row 1: Add User Button (prominent) -->
                <div class="flex justify-end mb-2">
                    <button onclick="showCreateUserModal()" 
                            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 shadow-sm">
                        <svg class="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        ${t('users.addButton')}
                    </button>
                </div>
                
                <!-- Row 2: Filters and Action Buttons -->
                <div class="flex items-end justify-between gap-4">
                    <!-- Left side: Search and Role Filter -->
                    <div class="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <!-- Search Field -->
                        <div>
                            <label for="user-search" class="block text-sm font-medium text-gray-700 mb-1">${t('users.search')}</label>
                            <input type="text" id="user-search" placeholder="${t('users.searchPlaceholder')}" 
                                   class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                   oninput="debouncedFilterUsers()">
                        </div>
                        
                        <!-- Role Filter -->
                        <div>
                            <label for="role-filter" class="block text-sm font-medium text-gray-700 mb-1">${t('users.filterByRole')}</label>
                            <select id="role-filter" class="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                    onchange="filterUsers()">
                                <option value="">${t('users.allRoles')}</option>
                                ${getCurrentUser()?.role === 'SuperAdmin' ? '<option value="SuperAdmin">SuperAdmin</option>' : ''}
                                <option value="Administrator">Administrator</option>
                                <option value="Team leader">Team leader</option>
                                <option value="Team member">Team member</option>
                                <option value="Team viewer">Team viewer</option>
                                <option value="Bookkeeper">Bookkeeper</option>
                                <option value="External contact">External contact</option>
                            </select>
                        </div>

                        <!-- Inactivity Filter -->
                        <div>
                            <label for="inactive-filter" class="block text-sm font-medium text-gray-700 mb-1">Inactive more than</label>
                            <select id="inactive-filter" class="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                    onchange="filterUsers()">
                                <option value="">All activity</option>
                                <option value="7d">1 week</option>
                                <option value="30d">1 month</option>
                                <option value="90d">3 months</option>
                                <option value="180d">6 months</option>
                                <option value="never">Never checked in</option>
                            </select>
                        </div>
                    </div>
                    
                    <!-- Right side: Action buttons -->
                    <div class="flex items-end space-x-2">
                        <button onclick="clearFilters()" 
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            ${t('users.clear')}
                        </button>
                        <button onclick="loadUsersData()" 
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            <svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            ${t('users.refresh')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Users Count Display -->
        <div class="bg-white shadow rounded-lg mt-3 px-6 py-3">
            <div id="users-count-display" class="text-sm text-gray-600">
                <span class="font-medium">${t('users.loading')}</span>
            </div>
        </div>
        
        <!-- Users List -->
        <div class="bg-white shadow rounded-lg mt-3">
            <div id="users-list" class="space-y-4">
                <div class="text-center py-4">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p class="mt-2 text-sm text-gray-500">Loading users...</p>
                </div>
            </div>
        </div>
        
        <!-- Loading indicator for infinite scroll -->
        <div id="users-loading-more" class="hidden mt-4 text-center py-4 bg-white shadow rounded-lg">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
            <p class="mt-2 text-sm text-gray-500">Loading more users...</p>
        </div>
        
        <!-- End of list indicator -->
        <div id="users-end-message" class="hidden mt-4 text-center py-3 text-sm text-gray-500">
            All users loaded
        </div>
    `;

    updatePageTitle('Setup - Users');
    updateActiveNavigation('users');
    loadUsersData(true);  // true = reset to first page
    setupInfiniteScroll();
}

/**
 * Load users data with pagination and filters
 */
export async function loadUsersData(reset = false) {
    try {
        // If reset, clear everything and start over
        if (reset) {
            usersInfiniteScroll.currentPage = 1;
            usersInfiniteScroll.allUsers = [];
            usersInfiniteScroll.hasMore = true;
        }
        
        // Prevent loading if already loading or no more data
        if (usersInfiniteScroll.isLoading) {
            console.log('Already loading, skipping...');
            return;
        }
        
        if (!usersInfiniteScroll.hasMore && !reset) {
            console.log('No more users to load');
            return;
        }
        
        usersInfiniteScroll.isLoading = true;
        
        // Show loading indicator
        if (!reset && usersInfiniteScroll.currentPage > 1) {
            const loadingMore = document.getElementById('users-loading-more');
            if (loadingMore) loadingMore.classList.remove('hidden');
        }
        
        // Get effective account ID for filtering
        const accountId = getEffectiveAccountId();
        if (!accountId) {
            showError('Please select an account to view users');
            usersInfiniteScroll.isLoading = false;
            const loadingMore = document.getElementById('users-loading-more');
            if (loadingMore) loadingMore.classList.add('hidden');
            return;
        }
        
        const roleFilter = document.getElementById('role-filter');
        const inactiveFilter = document.getElementById('inactive-filter');
        const searchInput = document.getElementById('user-search');
        
        const selectedRole = roleFilter ? roleFilter.value : '';
        const selectedInactive = inactiveFilter ? inactiveFilter.value : '';
        const searchTerm = searchInput ? searchInput.value.trim() : '';
        
        const params = new URLSearchParams();
        
        // Add account filtering with sub-accounts
        params.append('account_id', accountId);
        params.append('include_child_accounts', 'true');
        
        // Add pagination parameters
        params.append('skip', (usersInfiniteScroll.currentPage - 1) * usersInfiniteScroll.pageSize);
        params.append('limit', usersInfiniteScroll.pageSize);
        
        if (selectedRole) params.append('role', selectedRole);
        if (selectedInactive === 'never') {
            params.append('never_checked_in', 'true');
        } else if (selectedInactive) {
            params.append('inactive_more_than', selectedInactive);
        }
        if (searchTerm) params.append('search', searchTerm);
        
        // Add sorting parameters
        const currentSort = tableSortState.users;
        if (currentSort.column) {
            params.append('sort_by', currentSort.column);
            params.append('sort_direction', currentSort.direction);
        }
        
        const queryString = params.toString();
        const endpoint = queryString ? `/users/?${queryString}` : '/users/';
        
        const response = await apiCall(endpoint, {
            method: 'GET',
            headers: auth.getAuthHeaders()
        });

        // Handle the response
        let users = [];
        let totalCount = 0;
        
        if (Array.isArray(response)) {
            users = response;
            totalCount = response.length;
        } else if (response && Array.isArray(response.users)) {
            users = response.users;
            totalCount = response.total || response.users.length;
        } else if (response && response.data && Array.isArray(response.data)) {
            users = response.data;
            totalCount = response.total || response.data.length;
        }
        
        // Update state
        usersInfiniteScroll.totalUsers = totalCount;
        
        // Convert _id to id if needed
        if (users.length > 0 && users[0]._id && !users[0].id) {
            users = users.map(user => ({ ...user, id: user._id }));
        }
        
        // Append new users
        if (reset) {
            usersInfiniteScroll.allUsers = users;
        } else {
            usersInfiniteScroll.allUsers = [...usersInfiniteScroll.allUsers, ...users];
        }
        
        // Check if there are more users
        usersInfiniteScroll.hasMore = usersInfiniteScroll.allUsers.length < totalCount;
        
        // Hide loading indicator
        const loadingMore = document.getElementById('users-loading-more');
        if (loadingMore) loadingMore.classList.add('hidden');
        
        // Show/hide end message
        const endMessage = document.getElementById('users-end-message');
        if (endMessage) {
            if (usersInfiniteScroll.hasMore) {
                endMessage.classList.add('hidden');
            } else if (usersInfiniteScroll.allUsers.length > 0) {
                endMessage.classList.remove('hidden');
            }
        }
        
        // Display all accumulated users
        displayUsers(usersInfiniteScroll.allUsers);
        
        // Update count display
        updateUsersCount();
        
        // Increment page for next load
        usersInfiniteScroll.currentPage++;
        
        usersInfiniteScroll.isLoading = false;
        
    } catch (error) {
        usersInfiniteScroll.isLoading = false;
        showError('Failed to load users: ' + (error.message || 'Unknown error'));
        console.error('Error loading users:', error);
        
        const loadingMore = document.getElementById('users-loading-more');
        if (loadingMore) loadingMore.classList.add('hidden');
        
        const usersList = document.getElementById('users-list');
        if (usersList && usersInfiniteScroll.allUsers.length === 0) {
            usersList.innerHTML = `
                <div class="text-center py-8">
                    <p class="text-red-600">Error loading users</p>
                    <button onclick="loadUsersData(true)" class="mt-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
                        Retry
                    </button>
                </div>
            `;
        }
    }
}

/**
 * Display users in table format
 */
export function displayUsers(users) {
    const usersList = document.getElementById('users-list');
    if (!usersList) return;

    if (users.length === 0) {
        usersList.innerHTML = `
            <div class="text-center py-8">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 class="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                <p class="mt-1 text-sm text-gray-500">Get started by creating a new user.</p>
                <div class="mt-6">
                    <button onclick="showCreateUserModal()" 
                            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700">
                        <svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add User
                    </button>
                </div>
            </div>
        `;
        return;
    }

    usersList.innerHTML = `
        <div class="shadow ring-1 ring-black ring-opacity-5 md:rounded-lg overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-200" id="users-table-header">
                    <tr>
                        <th scope="col" class="sortable-header px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-200" onclick="sortUsersTable('full_name')">
                            <div class="flex items-center space-x-1">
                                <span>${t('common.name')}</span>
                                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                </svg>
                            </div>
                        </th>
                        <th scope="col" class="sortable-header px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-200" onclick="sortUsersTable('employee_id')">
                            <div class="flex items-center space-x-1">
                                <span>Employee ID</span>
                                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                </svg>
                            </div>
                        </th>
                        <th scope="col" class="sortable-header px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-200" onclick="sortUsersTable('account_name')">
                            <div class="flex items-center space-x-1">
                                <span>${t('users.account')}</span>
                                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                </svg>
                            </div>
                        </th>
                        <th scope="col" class="sortable-header px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-200" onclick="sortUsersTable('role')">
                            <div class="flex items-center space-x-1">
                                <span>${t('users.role')}</span>
                                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                </svg>
                            </div>
                        </th>
                        <th scope="col" class="sortable-header px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-200" onclick="sortUsersTable('nfc_card_id')">
                            <div class="flex items-center space-x-1">
                                <span>${t('users.nfcCardId')}</span>
                                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                </svg>
                            </div>
                        </th>
                        <th scope="col" class="sortable-header px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-200" onclick="sortUsersTable('last_checkin_at')">
                            <div class="flex items-center space-x-1">
                                <span>Last check-in</span>
                                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                </svg>
                            </div>
                        </th>
                        <th scope="col" class="sortable-header px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-200" onclick="sortUsersTable('last_login')">
                            <div class="flex items-center space-x-1">
                                <span>Last login</span>
                                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                </svg>
                            </div>
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-200">
                            ${t('users.status')}
                        </th>
                        <th scope="col" class="relative px-6 py-3 bg-gray-200">
                            <span class="sr-only">${t('common.actions')}</span>
                        </th>
                    </tr>
                </thead>
                <tbody id="users-table-body" class="bg-white divide-y divide-gray-200">
                    ${generateUserRows(users)}
                </tbody>
            </table>
        </div>
    `;
}

/**
 * Generate HTML rows for users
 */
function generateUserRows(users) {
    return users.map(user => {
        const isTeamMember = user.role === 'Team member';
        const isInvitationPending = !user.password_set && user.invitation_token && !isTeamMember;
        const isActive = user.is_active && (user.password_set || isTeamMember);
        
        let statusBadge, statusText;
        if (isTeamMember) {
            statusBadge = 'bg-gray-100 text-gray-800';
            statusText = 'No Access';
        } else if (isInvitationPending) {
            statusBadge = 'bg-yellow-100 text-yellow-800';
            statusText = 'Invited';
        } else if (isActive) {
            statusBadge = 'bg-green-100 text-green-800';
            statusText = 'Access';
        } else {
            statusBadge = 'bg-red-100 text-red-800';
            statusText = 'No Access';
        }
        
        return `
        <tr class="hover:bg-gray-50">
            <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                    <div class="flex-shrink-0">
                        <div class="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                            <span class="text-sm font-medium text-primary-600">
                                ${(user.full_name || user.username).charAt(0).toUpperCase()}
                            </span>
                        </div>
                    </div>
                    <div class="ml-4">
                        <div class="text-sm font-medium text-gray-900 cursor-pointer hover:text-primary-600 transition-colors" onclick="editUser('${user.id}')">
                            ${user.full_name || user.username}
                        </div>
                        ${user.department ? `<div class="text-xs text-gray-500">${user.department}</div>` : ''}
                    </div>
                </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">
                ${user.employee_id || '<span class="text-gray-400">-</span>'}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                ${user.account_name || 'N/A'}
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeClass(user.role)}">
                    ${user.role}
                </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">
                ${(() => {
                    const ids = (user.nfc_card_ids && user.nfc_card_ids.length)
                        ? user.nfc_card_ids
                        : (user.nfc_card_id ? [user.nfc_card_id] : []);
                    if (!ids.length) return '<span class="text-gray-400">-</span>';
                    if (ids.length === 1) return ids[0];
                    return `${ids[0]} <span class="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600" title="${ids.join(', ')}">+${ids.length - 1}</span>`;
                })()}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                ${formatLastCheckin(user.last_checkin_at)}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                ${formatLastLogin(user.last_login, isTeamMember)}
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge}">
                    ${statusText}
                </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                ${isInvitationPending ? `
                <button onclick="resendInvitation('${user.id}', '${user.full_name || user.username}')" 
                        class="text-primary-600 hover:text-primary-900 text-xs font-medium">
                    Resend Invitation
                </button>
                ` : ''}
            </td>
        </tr>
        `;
    }).join('');
}

/**
 * Update users count display
 */
export function updateUsersCount() {
    const countElement = document.getElementById('users-count-display');
    if (!countElement) return;
    
    const displayedCount = usersInfiniteScroll.allUsers.length;
    const totalCount = usersInfiniteScroll.totalUsers;
    
    const roleFilter = document.getElementById('role-filter');
    const inactiveFilter = document.getElementById('inactive-filter');
    const searchInput = document.getElementById('user-search');
    
    const hasRoleFilter = roleFilter && roleFilter.value !== '';
    const hasInactiveFilter = inactiveFilter && inactiveFilter.value !== '';
    const hasSearch = searchInput && searchInput.value.trim() !== '';
    const hasFilters = hasRoleFilter || hasSearch || hasInactiveFilter;
    
    if (hasFilters) {
        let filterDescription = [];
        if (hasSearch) filterDescription.push(`search: "${searchInput.value}"`);
        if (hasRoleFilter) filterDescription.push(`role: ${roleFilter.value}`);
        if (hasInactiveFilter) {
            const inactiveLabelMap = {
                '7d': 'inactive > 1 week',
                '30d': 'inactive > 1 month',
                '90d': 'inactive > 3 months',
                '180d': 'inactive > 6 months',
                'never': 'never checked in'
            };
            filterDescription.push(inactiveLabelMap[inactiveFilter.value] || inactiveFilter.value);
        }
        
        countElement.innerHTML = `
            <div class="flex items-center space-x-2">
                <span class="font-medium">${t('common.showing')} ${displayedCount.toLocaleString()} ${t('common.of')} ${totalCount.toLocaleString()} ${t('users.users')}</span>
                <span class="text-gray-400">•</span>
                <span class="text-blue-600">${filterDescription.join(', ')}</span>
            </div>
        `;
    } else {
        const loadingMore = displayedCount < totalCount && usersInfiniteScroll.hasMore;
        countElement.innerHTML = `
            <div class="flex items-center space-x-2">
                <span class="font-medium">${t('common.total')}: ${totalCount.toLocaleString()} ${t('users.users')}</span>
                ${loadingMore ? `<span class="text-gray-400">• <span class="italic">showing first ${displayedCount.toLocaleString()}, scroll for more</span></span>` : ''}
            </div>
        `;
    }
}

/**
 * Setup infinite scroll listener
 */
export function setupInfiniteScroll() {
    let scrollTimeout;
    
    window.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
            const scrollPosition = window.innerHeight + window.scrollY;
            const documentHeight = document.documentElement.scrollHeight;
            const threshold = 300;
            
            if (scrollPosition >= documentHeight - threshold) {
                if (usersInfiniteScroll.hasMore && !usersInfiniteScroll.isLoading) {
                    console.log('Near bottom, loading more users...');
                    loadUsersData(false);
                }
            }
        }, 100);
    });
}

/**
 * Table sorting function
 */
export function sortUsersTable(column) {
    const currentSort = tableSortState.users;
    
    if (currentSort.column === column) {
        currentSort.direction = currentSort.direction === 'asc' ? 'desc' : 'asc';
    } else {
        currentSort.column = column;
        currentSort.direction = 'asc';
    }
    
    loadUsersData(true);
}

/**
 * Debounced search filter
 */
let searchDebounceTimer = null;
export function debouncedFilterUsers() {
    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
    }
    
    searchDebounceTimer = setTimeout(() => {
        filterUsers();
    }, 500);
}

/**
 * Filter users
 */
export function filterUsers() {
    loadUsersData(true);
}

/**
 * Clear all filters
 */
export function clearFilters() {
    const searchInput = document.getElementById('user-search');
    const roleFilter = document.getElementById('role-filter');
    const inactiveFilter = document.getElementById('inactive-filter');
    
    if (searchInput) searchInput.value = '';
    if (roleFilter) roleFilter.value = '';
    if (inactiveFilter) inactiveFilter.value = '';
    
    loadUsersData(true);
}

// Backend sends naive UTC ISO strings (no 'Z'/offset); treat as UTC before showing in local time
function parseUtcTimestamp(iso) {
    if (!iso) return null;
    let t = iso;
    if (typeof t === 'string' && !t.endsWith('Z') && !t.includes('+')) t += 'Z';
    const dt = new Date(t);
    return Number.isNaN(dt.getTime()) ? null : dt;
}

// Fixed-format DD/MM/YYYY HH:MM (matches formatDateTime in utils/helpers.js and
// the Alarms list) wrapped in tabular-nums so digit widths stay constant down
// the column even in a proportional font.
function formatDateTimeAligned(dt) {
    const formatted = dt.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    }).replace(',', '');
    return `<span class="tabular-nums">${formatted}</span>`;
}

function formatLastCheckin(lastCheckinIso) {
    if (!lastCheckinIso) {
        return '<span class="text-gray-400">Never</span>';
    }

    const dt = parseUtcTimestamp(lastCheckinIso);
    if (!dt) {
        return '<span class="text-gray-400">-</span>';
    }

    return formatDateTimeAligned(dt);
}

function formatLastLogin(lastLoginIso, isTeamMember) {
    if (!lastLoginIso) {
        // Team members have no login credentials, so "never" is expected, not a red flag
        return isTeamMember
            ? '<span class="text-gray-400">N/A</span>'
            : '<span class="text-amber-600">Never logged in</span>';
    }

    const dt = parseUtcTimestamp(lastLoginIso);
    if (!dt) {
        return '<span class="text-gray-400">-</span>';
    }

    return formatDateTimeAligned(dt);
}

/**
 * Get role badge CSS class
 */
function getRoleBadgeClass(role) {
    const roleMap = {
        'SuperAdmin': 'bg-purple-100 text-purple-800',
        'Administrator': 'bg-purple-100 text-purple-800',
        'Team leader': 'bg-blue-100 text-blue-800',
        'Team member': 'bg-green-100 text-green-800',
        'Team viewer': 'bg-teal-100 text-teal-800',
        'Bookkeeper': 'bg-amber-100 text-amber-800',
        'External contact': 'bg-gray-100 text-gray-800',
        'Admin': 'bg-red-100 text-red-800',
        'Manager': 'bg-yellow-100 text-yellow-800',
        'User': 'bg-green-100 text-green-800'
    };
    return roleMap[role] || 'bg-green-100 text-green-800';
}

// Make functions globally accessible for onclick handlers
if (typeof window !== 'undefined') {
    window.loadUsersData = loadUsersData;
    window.debouncedFilterUsers = debouncedFilterUsers;
    window.filterUsers = filterUsers;
    window.clearFilters = clearFilters;
    window.sortUsersTable = sortUsersTable;
}

export default {
    showUsers,
    loadUsersData,
    displayUsers,
    updateUsersCount,
    setupInfiniteScroll,
    sortUsersTable,
    debouncedFilterUsers,
    filterUsers,
    clearFilters
};
